import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Reset.css";

// image assets for picture pattern system
import elephant from "../assets/login/elephant.png";
import tuktuk from "../assets/login/tuktuk.png";
import lion from "../assets/login/lion.png";
import turtle from "../assets/login/turtle.png";
import palmtree from "../assets/login/palmtree.png";
import monkey from "../assets/login/monkey.png";
import flag from "../assets/login/flag.png";
import mango from "../assets/login/mango.png";
import crab from "../assets/login/crab.png";

// backend base URL (local fallback if env not set)
const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// available images for pattern selection
const pictureChoices = [
  { name: "Elephant", src: elephant },
  { name: "Tuk Tuk", src: tuktuk },
  { name: "Lion", src: lion },
  { name: "Turtle", src: turtle },
  { name: "Palm Tree", src: palmtree },
  { name: "Monkey", src: monkey },
  { name: "Flag", src: flag },
  { name: "Mango", src: mango },
  { name: "Crab", src: crab },
];

export default function Reset() {
  const goToPage = useNavigate();

  // step 1 = enter name, step 2 = choose new pattern
  const [step, setStep] = useState(1);

  const [name, setName] = useState("");
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // handles selecting/removing pictures from pattern
  function handleSelect(picture) {
    const selectedIndex = selectedPictures.indexOf(picture.name);

    // remove if already selected
    if (selectedIndex !== -1) {
      setSelectedPictures(
        selectedPictures.filter((item) => item !== picture.name)
      );
      return;
    }

    // limit to 3 selections (required by backend)
    if (selectedPictures.length < 3) {
      setSelectedPictures([...selectedPictures, picture.name]);
    }
  }

  // sends new pattern to backend
  async function handleReset() {
    const trimmedName = name.trim();

    if (!trimmedName || selectedPictures.length !== 3) {
      alert("Enter your name and pick 3 pictures!");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/reset-pattern`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          new_pattern: selectedPictures,
        }),
      });

      const resultData = await response.json();

      // handle backend errors
      if (!response.ok) {
        alert(resultData.error || "Could not reset your pattern.");
        return;
      }

      // success → redirect to login
      alert("Your new pattern has been saved!");
      goToPage("/loading", { state: { to: "/login" } });
    } catch (error) {
      console.error("Reset pattern error:", error);
      alert("Server error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="resetPage">
      {/* back to landing */}
      <button
        className="backBtn"
        onClick={() => goToPage("/loading", { state: { to: "/" } })}
        type="button"
      >
        ← Back
      </button>

      {/* STEP 1: enter name */}
      {step === 1 && (
        <div className="stepContainer">
          <h1 className="resetTitle">Make a New Secret Pattern</h1>

          <input
            className="nameInput fun"
            placeholder="Type your name here..."
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <button
            className="nextBtn"
            onClick={() => {
              if (!name.trim()) {
                alert("Enter your name first!");
                return;
              }

              setStep(2);
            }}
            type="button"
          >
            Next →
          </button>
        </div>
      )}

      {/* STEP 2: choose new picture pattern */}
      {step === 2 && (
        <>
          <h1 className="resetTitle">
            Hi {name}! Choose a New Pattern
          </h1>

          <p className="instruction">
            Pick 3 pictures in order
          </p>

          {/* preview of selected order */}
          <div className="selectedPreview">
            {selectedPictures.length === 0 ? (
              <span className="selectedPlaceholder">
                No pictures chosen yet
              </span>
            ) : (
              selectedPictures.map((item, index) => (
                <span key={item} className="selectedItem">
                  {index + 1}. {item}
                </span>
              ))
            )}
          </div>

          {/* selectable image grid */}
          <div className="imageGrid">
            {pictureChoices.map((picture) => {
              const selectedIndex =
                selectedPictures.indexOf(picture.name);
              const isSelected = selectedIndex !== -1;

              return (
                <button
                  key={picture.name}
                  className={`imageCard ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(picture)}
                  type="button"
                  disabled={isSubmitting}
                >
                  {/* shows order number */}
                  {isSelected && (
                    <div className="numberBadge">
                      {selectedIndex + 1}
                    </div>
                  )}

                  <img src={picture.src} alt={picture.name} />
                  <p>{picture.name}</p>
                </button>
              );
            })}
          </div>

          <button
            className="submitBtn"
            onClick={handleReset}
            type="button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save New Pattern"}
          </button>
        </>
      )}
    </div>
  );
}